// SharePage — a referrer's own one-click share page (job mct_ee41e1ee-080).
//
//   /share/:token   linked from every referrer email and the thank-you page
//
// The token is unguessable and is the only way in (never an email or a code).
// The page shows the referrer their own short link, how many registered
// through it and the next reward; one-click share buttons for LinkedIn,
// Facebook, X, WhatsApp and email, each carrying that same short link (never
// the page it lands on); copy link; and the ready-made images Command Centre
// supplies (nothing at all when there are none). It shows no email address
// and nothing about the people they referred. The worker builds every share
// address (referrer-emails-core.ts shareTargets), so the page only renders.
//
// Several posts (job mct_5b9caba4-a4d): the referrer picks one; LinkedIn and
// email use its LinkedIn version, X and WhatsApp its short one, and the copy
// buttons copy the matching version with their link. The worker supplies the
// copy text too, so no post wording and no link is ever typed here.
//
// Make your card (job mct_7e138a6a-e47): when the share kit carries Command
// Centre's card pack, a section lets the referrer make their own "I'm
// speaking at" / "I'm attending" card (the worker decides which), with an
// optional photo that never leaves their device. No pack, no section.

import { useEffect, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import ShareCardMaker from './components/ShareCardMaker';
import { fetchSharePage, type SharePageData, type SharePagePost } from './api';

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 32, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.15, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 22px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '22px 22px 24px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)', marginBottom: 18 };
const shareBtn: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 120, padding: '12px 18px', borderRadius: 10,
  fontWeight: 700, fontSize: 15, textDecoration: 'none', color: '#fff', margin: '0 8px 10px 0',
};
const small: CSSProperties = { fontSize: 14, color: '#6b6b80', lineHeight: 1.5 };
const pick = (on: boolean): CSSProperties => ({
  padding: '8px 14px', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: 'pointer', margin: '0 8px 8px 0',
  border: on ? '2px solid #0A0A2E' : '1px solid #d4d4dc', background: on ? '#0A0A2E' : '#fff', color: on ? '#fff' : '#0A0A2E',
});
const postBox: CSSProperties = {
  whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 15, lineHeight: 1.5, color: '#2a2a40',
  background: '#f7f7fa', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', margin: '6px 0 10px',
};

/** The posts to offer: the worker's list, or (from a worker older than posts)
 *  the one post its shareText and targets describe. */
function postsOf(d: SharePageData): SharePagePost[] {
  if (d.posts?.length) return d.posts;
  return [{
    label: 'Post', linkedin: d.shareText, short: d.shareText, targets: d.targets,
    copy: { linkedin: `${d.shareText}\n\n${d.shortUrl}`, short: `${d.shareText} ${d.shortUrl}` },
  }];
}

const NETWORKS: Array<{ key: keyof SharePagePost['targets']; label: string; colour: string }> = [
  { key: 'linkedin', label: 'LinkedIn', colour: '#0A66C2' },
  { key: 'facebook', label: 'Facebook', colour: '#1877F2' },
  { key: 'x', label: 'X', colour: '#000000' },
  { key: 'whatsapp', label: 'WhatsApp', colour: '#128C4B' },
  { key: 'email', label: 'Email', colour: '#F05A28' },
];

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <TakerShell footerLabel="The Successful Bookkeeper">
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '44px 16px 80px' }}>{children}</div>
    </TakerShell>
  );
}

export function SharePage() {
  const { token = '' } = useParams();
  const [data, setData] = useState<SharePageData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading');
  const [copied, setCopied] = useState<string | null>(null);
  const [chosen, setChosen] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchSharePage(token).then((d) => {
      if (!alive) return;
      setData(d);
      setState(d ? 'ready' : 'missing');
    });
    return () => { alive = false; };
  }, [token]);

  if (state === 'loading') return <Shell><p style={small}>Loading your share page…</p></Shell>;
  if (state === 'missing' || !data) {
    return (
      <Shell>
        <h1 style={h1}>This share page isn't available</h1>
        <p style={lead}>The link may be incomplete. Open the most recent referral email from us and use the Share again button there.</p>
      </Shell>
    );
  }

  const shown = data.shortUrl.replace(/^https:\/\//, '');
  const posts = postsOf(data);
  const post = posts[Math.min(chosen, posts.length - 1)];
  const sameText = post.linkedin === post.short;
  async function copy(what: string, value: string) {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(what);
      setTimeout(() => setCopied((c) => (c === what ? null : c)), 2000);
    } catch {
      /* the words and link stay visible to copy by hand */
    }
  }

  return (
    <Shell>
      <h1 style={h1}>{data.firstName ? `Thank you, ${data.firstName}` : 'Thank you for sharing'}</h1>
      <p style={lead}>
        Everyone who registers for {data.eventTitle} through your link is counted to you.
        {data.nextTreat && <> Reach {data.nextTreat.atReferrals} and you get <strong>{data.nextTreat.label}</strong>.</>}
      </p>

      <div style={card}>
        <p style={{ margin: '0 0 10px', fontWeight: 700 }}>Your link</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <code style={{ fontSize: '1rem', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, wordBreak: 'break-all' }}>{shown}</code>
          <button type="button" className="btn btn-secondary" onClick={() => copy('link', data.shortUrl)}>{copied === 'link' ? 'Copied' : 'Copy link'}</button>
        </div>
        <p style={{ ...small, margin: '14px 0 0' }}>
          {data.count === 0
            ? 'Nobody has registered through your link yet.'
            : `${data.count} ${data.count === 1 ? 'person has' : 'people have'} registered through your link.`}
          {data.nextTreat && data.count > 0 && ` ${data.nextTreat.more} more for ${data.nextTreat.label}.`}
        </p>
      </div>

      <div style={card}>
        <p style={{ margin: '0 0 6px', fontWeight: 700 }}>Share in one click</p>
        <p style={{ ...small, margin: '0 0 14px' }}>
          {posts.length > 1 ? 'Pick a post. ' : ''}Each button opens it ready-made with your link already in it. Change the words if you like.
        </p>
        {posts.length > 1 && (
          <div role="radiogroup" aria-label="Choose a post" style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 6 }}>
            {posts.map((p, i) => (
              <button key={i} type="button" role="radio" aria-checked={p === post} onClick={() => setChosen(i)} style={pick(p === post)}>
                {p.label}
              </button>
            ))}
          </div>
        )}
        <p style={{ ...small, margin: '8px 0 0', fontWeight: 700 }}>{sameText ? 'The post' : 'For LinkedIn and email'}</p>
        <div style={postBox}>{post.linkedin}</div>
        {!sameText && (
          <>
            <p style={{ ...small, margin: '8px 0 0', fontWeight: 700 }}>Short version, for X and WhatsApp</p>
            <div style={postBox}>{post.short}</div>
          </>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 6 }}>
          {NETWORKS.map((n) => (
            <a key={n.key} href={post.targets[n.key]} target={n.key === 'email' ? undefined : '_blank'} rel="noopener noreferrer"
              style={{ ...shareBtn, background: n.colour }}>
              {n.label}
            </a>
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={() => copy('linkedin', post.copy.linkedin)}>
            {copied === 'linkedin' ? 'Copied' : sameText ? 'Copy post with link' : 'Copy LinkedIn post'}
          </button>
          {!sameText && (
            <button type="button" className="btn btn-secondary" onClick={() => copy('short', post.copy.short)}>
              {copied === 'short' ? 'Copied' : 'Copy short post'}
            </button>
          )}
        </div>
      </div>

      {data.cards && <ShareCardMaker token={token} cards={data.cards} shareText={post.copy.short} />}

      {data.images.length > 0 && (
        <div style={card}>
          <p style={{ margin: '0 0 6px', fontWeight: 700 }}>Ready-made images</p>
          <p style={{ ...small, margin: '0 0 14px' }}>Download one and post it with your link.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
            {data.images.map((img) => (
              <a key={img.url} href={img.url} download target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#0A0A2E' }}>
                <img src={img.url} alt={img.label} style={{ width: '100%', borderRadius: 10, border: '1px solid #e5e7eb', display: 'block' }} />
                <span style={{ ...small, display: 'block', marginTop: 6 }}>{img.label}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}
